import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Receipt, Loader2, Search } from "lucide-react";

const KIND_META = {
  signup: { label: "Onboarding", cls: "bg-sky-500/15 text-sky-200 border-sky-400/30" },
  addon: { label: "Addon", cls: "bg-violet-500/15 text-violet-200 border-violet-400/30" },
  renewal: { label: "Renewal", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
  app_package: { label: "App package", cls: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "signup", label: "Onboarding" },
  { id: "addon", label: "Addons" },
  { id: "renewal", label: "Renewals" },
  { id: "app_package", label: "App packages" },
];

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function fmt(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  const cls = s === "PAID" || s === "PROVISIONED" || s === "COMPLETED"
    ? "bg-emerald-500/15 text-emerald-200"
    : "bg-slate-700/60 text-slate-300";
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{s || "—"}</span>;
}

export default function SuperPurchases() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState("");

  const load = (nextKind = kind, nextQ = q) => {
    setLoading(true);
    const params = { kind: nextKind, limit: 400 };
    if (nextQ) params.q = nextQ;
    api.get("/platform/purchases", { params })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load("all", ""); }, []);

  const items = data?.items || [];

  return (
    <div className="space-y-6 max-w-6xl" data-testid="purchase-billing-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100">Purchase & billing</h1>
        <p className="text-slate-500 text-sm mt-1">
          Every online purchase and paid onboarding. Coupon checkouts show as coupon payment; gateway payments show Cashfree with the total.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`purchase-filter-${f.id}`}
              onClick={() => { setKind(f.id); load(f.id, q); }}
              className={`text-xs px-3 py-1.5 rounded-full border ${kind === f.id ? "bg-indigo-500/20 text-indigo-200 border-indigo-400/40" : "border-slate-700 text-slate-400 hover:text-white"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <form
          className="flex items-center gap-2 ml-auto"
          onSubmit={(e) => { e.preventDefault(); setQ(draft.trim()); load(kind, draft.trim()); }}
        >
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Search buyer, store, coupon, order…"
              data-testid="purchase-search"
              className="bg-slate-900 border-slate-700 text-slate-100 pl-8 w-64 h-9 text-sm"
            />
          </div>
        </form>
      </div>

      {data && (
        <div className="grid sm:grid-cols-3 gap-3">
          <Card className="bg-[#0f1623] border-slate-800 p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Records</div>
            <div className="text-xl font-bold text-white mt-1">{data.count ?? items.length}</div>
          </Card>
          <Card className="bg-[#0f1623] border-slate-800 p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Collected (GST incl.)</div>
            <div className="text-xl font-bold text-white mt-1">{rupee(data.paid_total)}</div>
          </Card>
          <Card className="bg-[#0f1623] border-slate-800 p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Coupon onboardings</div>
            <div className="text-xl font-bold text-white mt-1">{data.coupon_count ?? 0}</div>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading payments…</div>
      ) : items.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 p-10 text-center">
          <Receipt className="w-8 h-8 text-slate-600 mx-auto" />
          <p className="text-slate-400 mt-3 text-sm">No purchases yet.</p>
        </Card>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-hidden bg-[#0f1623]">
          <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-2.5 bg-slate-900/80 border-b border-slate-800 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <div className="col-span-3">Buyer / store</div>
            <div className="col-span-3">Purchase</div>
            <div className="col-span-2">When</div>
            <div className="col-span-3">Payment gateway</div>
            <div className="col-span-1 text-right">Status</div>
          </div>
          <div className="divide-y divide-slate-800/80">
            {items.map((it, i) => {
              const meta = KIND_META[it.kind] || KIND_META.signup;
              return (
                <div key={it.order_id || i} className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-3 px-4 py-3.5" data-testid="purchase-row">
                  <div className="lg:col-span-3 min-w-0">
                    <div className="font-semibold text-white text-sm truncate">{it.buyer_name || it.buyer_email || "—"}</div>
                    <div className="text-xs text-slate-400 truncate">{it.buyer_email || ""}</div>
                    <div className="text-[11px] text-slate-500 truncate">{it.store_name || it.store_slug ? `${it.store_name || "Store"} · ${it.store_slug || ""}` : ""}</div>
                  </div>
                  <div className="lg:col-span-3 min-w-0">
                    <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                    <div className="text-sm text-slate-200 mt-1 truncate">{it.title}</div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{it.order_id}</div>
                  </div>
                  <div className="lg:col-span-2 text-sm text-slate-400">{fmt(it.paid_at || it.created_at)}</div>
                  <div className="lg:col-span-3">
                    <div className={`text-sm font-semibold ${it.payment_kind === "coupon" ? "text-amber-200" : "text-indigo-200"}`} data-testid="purchase-payment-label">
                      {it.payment_label}
                    </div>
                    {it.coupon_code ? (
                      <div className="text-[11px] text-amber-400/90 mt-0.5">Code {it.coupon_code}</div>
                    ) : (
                      <div className="text-[11px] text-slate-500 mt-0.5">Payment Gateway · {it.gateway_name || "Cashfree"}</div>
                    )}
                  </div>
                  <div className="lg:col-span-1 flex lg:justify-end items-start"><StatusBadge status={it.status} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
